import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeftIcon } from '@heroicons/react/24/outline'
import AccountingToolPage from '@/components/AccountingToolPage'
import ReportMonthSelect from '@/components/ReportMonthSelect'
import { usePageAccess } from '@/hooks/usePageAccess'
import { getReportHtml, listReports } from '@/services/monthlyPerformance'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import { memberViewReport, monthsToSelectOptions } from './monthlyPerformanceUtils'
import { reportingReportPath } from './reportingPaths'
import { useMonthlyPerformanceMonth } from './useMonthlyPerformanceMonth'

export default function MonthlyPerformanceMembers() {
  const { canEdit } = usePageAccess('monthly-performance')
  const { months, month, setMonth } = useMonthlyPerformanceMonth()
  const [html, setHtml] = useState('')
  const [loading, setLoading] = useState(true)
  const [iframeHeight, setIframeHeight] = useState(480)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const activeLoadMonthRef = useRef<string | null>(null)

  const handleIframeLoad = () => {
    const doc = iframeRef.current?.contentDocument
    if (doc?.body) {
      setIframeHeight(doc.body.scrollHeight)
    }
  }

  const monthOptions = useMemo(() => monthsToSelectOptions(months), [months])

  const loadMonthData = useCallback(async (targetMonth: string) => {
    activeLoadMonthRef.current = targetMonth
    setLoading(true)
    try {
      const reports = await listReports(targetMonth)
      if (activeLoadMonthRef.current !== targetMonth) return
      const visible = memberViewReport(reports, { includeDraftPreview: canEdit })
      if (visible) {
        const reportHtml = await getReportHtml(visible.id)
        if (activeLoadMonthRef.current !== targetMonth) return
        setHtml(reportHtml)
        setIframeHeight(480)
      } else {
        setHtml('')
        setIframeHeight(480)
      }
    } catch (error) {
      if (activeLoadMonthRef.current !== targetMonth) return
      setHtml('')
      toast.error(formatApiError(error, 'Failed to load performance report'))
    } finally {
      if (activeLoadMonthRef.current === targetMonth) {
        setLoading(false)
      }
    }
  }, [canEdit])

  useEffect(() => {
    if (!month) return
    void loadMonthData(month)
  }, [month, loadMonthData])

  return (
    <AccountingToolPage
      title="Reporting"
      description="Performance report shared with members"
    >
      <div className="mx-auto flex w-full max-w-[720px] flex-col gap-6">
        {canEdit ? (
          <Link
            to={reportingReportPath(month)}
            className="inline-flex w-fit items-center gap-1 text-sm font-medium text-brand-700 hover:underline"
          >
            <ChevronLeftIcon className="h-4 w-4" aria-hidden />
            Back to Admin view
          </Link>
        ) : null}

        <div className="min-w-[220px]">
          <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="mp-members-month">
            As-of month
          </label>
          <ReportMonthSelect
            id="mp-members-month"
            options={monthOptions}
            value={month}
            onChange={setMonth}
          />
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : html ? (
          <iframe
            ref={iframeRef}
            sandbox="allow-same-origin"
            title="Monthly performance report"
            srcDoc={html}
            className="mx-auto w-[680px] max-w-full border border-gray-200 bg-white"
            style={{ height: iframeHeight }}
            onLoad={handleIframeLoad}
          />
        ) : (
          <p className="text-sm text-gray-600">
            No performance report has been sent for this month yet.
          </p>
        )}
      </div>
    </AccountingToolPage>
  )
}
